(ns ui.subscription-statement.page
  "UI that similar to a bank account statement show the customer what has
   increased or decreased their credit-balance over time."
  (:require [ui.app-frame :as app-frame]
            [parts.replicant.query :as query]
            [tick.core :as t]
            [tick.locale-en-us]
            ))

(defn build-query
  [{:keys [year month]}]
  {:query/kind :query/subscription-statement
   :query/data {:year year
                :month month}
   })

(defn format-date
  [date format]
  (t/format (t/formatter format)
            (t/zoned-date-time (t/instant date)))
  )

(defn two-decimals
  [number]
  (some-> number
          (.toFixed 2)))

(defn amount-decimals
  "Two decimals, except below one cent: those get two significant digits
   (4 to 8 decimals). A short text-to-speech generation costs a third of a
   cent, which two decimals render as -0.00."
  [number]
  (let [magnitude (js/Math.abs number)]
    (if (and (pos? magnitude)
             (< magnitude 0.01))
      (-> (js/Math.log10 magnitude)
          (js/Math.floor)
          (-)
          (inc)
          (max 4)
          (min 8))
      2)))

(defn format-amount
  [number]
  (some-> number
          (.toFixed (amount-decimals number))))

(defn plus-prefix
  [number]
  (if (pos? number)
    (str "+" number)
    number))

(defn discount-badge
  "Displays a discount badge based on discount percentage."
  [discount-pct]
  (when (and discount-pct (pos? discount-pct))
    [:span
     {:class ["inline-flex" "items-center" "rounded-md" "bg-green-50" "px-2" "py-1" "text-xs" "font-medium" "text-green-700" "ring-1" "ring-inset" "ring-green-600/20" "ml-2"]}
     (str (Math/round (* discount-pct 100)) "% OFF")]))

(defn format-total-with-discount
  "Shows original price with strikethrough and discounted price if discount applies."
  [units unit-price total discount]
  (if (and discount (pos? discount))
    (let [original-total (* units unit-price)]
      [:div
       {:class ["flex" "flex-col" "gap-1"]}
       [:span
        {:class ["text-gray-400" "line-through" "text-xs"]}
        (plus-prefix (format-amount original-total))]
       [:span
        {:class (if (neg? total)
                  "text-red-500 font-semibold"
                  "text-green-600 font-semibold")}
        (plus-prefix (format-amount total))]])
    [:span
     {:class (if (neg? total)
               "text-red-500"
               "text-green-500")}
     (plus-prefix (format-amount total))]))

(def header-css
  ["py-3.5" "px-3" "text-left" "text-sm" "font-semibold" "text-gray-900"])

(def cell-css
  ["py-4" "px-3" "text-sm" "font-medium" "text-gray-900"])

(defn date-cell
  "The daily fees carry no time of day."
  [line-item]
  (format-date (:date line-item)
               (if (#{:instagram-connection
                      :storage-flat-fee}
                    (:feature line-item))
                 "yyyy-MM-dd"
                 "yyyy-MM-dd HH:mm")))

(defn total-cell
  [line-item]
  (format-total-with-discount
    (:units line-item)
    (:unit-price line-item)
    (:total line-item)
    (:discount line-item)))

(defn balance-cell
  [line-item]
  (let [balance (:credit-balance line-item)]
    [:span
     {:class (if (neg? balance)
               "text-red-500"
               "text-black")}
     (two-decimals balance)]))

(def columns
  "One map per column: the header, the function that renders the cell of
   a line-item, the classes header and cells share (`:class`) and the
   classes of the cells alone (`:cell-class`). Dates and numbers stay on
   one line, the description takes the remaining width (`w-full`) and
   wraps like the headers, so the table fits its container without a
   horizontal scrollbar. Phones get the units, unit price and discount
   columns hidden (the total still shows a discount, original price struck
   through) and may still scroll: the four remaining columns need more
   than the ~250px that the page padding leaves there."
  [{:name "Date"
    :cell-class ["whitespace-nowrap"]
    :cell date-cell}
   {:name "Description"
    :class ["w-full"]
    :cell :description}
   {:name "Units"
    :class ["hidden" "sm:table-cell"]
    :cell-class ["whitespace-nowrap"]
    :cell :units}
   {:name "Unit Price"
    :class ["hidden" "sm:table-cell"]
    :cell-class ["whitespace-nowrap"]
    :cell (comp two-decimals :unit-price)}
   {:name "Discount"
    :class ["hidden" "sm:table-cell"]
    :cell-class ["whitespace-nowrap"]
    :cell (comp discount-badge :discount)}
   {:name "Total"
    :cell-class ["whitespace-nowrap"]
    :cell total-cell}
   {:name "Credit Balance"
    :cell-class ["whitespace-nowrap"]
    :cell balance-cell}])

(defn table
  [{:keys [line-items]}]
  [:div
   {:class "mt-8 overflow-x-auto shadow ring-1 ring-black/5 sm:rounded-lg"}
   [:table
    {:class "w-full divide-y divide-gray-300"}
    [:thead
     {:class "bg-gray-50"}
     [:tr
      (map
        (fn [{:keys [name class]}]
          [:th
           {:scope "col"
            :class (concat header-css class)}
           name])
        columns)]]
    [:tbody
     {:class "divide-y divide-gray-200 bg-white"}
     (map
       (fn [line-item]
         [:tr
          (map
            (fn [{:keys [cell class cell-class]}]
              [:td
               {:class (concat cell-css class cell-class)}
               (cell line-item)])
            columns)])
       line-items)]]])

(defn month-select
  [state]
  (let [classes ["appearance-none" "rounded-md" "bg-white" "py-1.5" "pl-3" "pr-8" "text-base" "text-gray-900" "outline" "outline-1" "-outline-offset-1" "outline-gray-300" "focus:outline" "focus:outline-2" "focus:-outline-offset-2" "focus:outline-indigo-600" "sm:text-sm/6"]]
    [:div.flex.mt-6
     [:div
      [:label
       {:for "year"
        :class ["block" "text-sm/6" "font-medium" "text-gray-900"]}
       "Year"]
      [:div
       {:class "mt-1"}
       [:select
        {:id "year"
         :name "year"
         :on {:change [[:store/assoc-in [::params :year] :event/target.value-int]
                       [:data/query {:query/kind :query/subscription-statement
                                     :query/data (assoc (::params state)
                                                        :year :event/target.value-int)
                                     }]]}
         :class classes}
        (map
          (fn [year]
            [:option
             {:selected (= (:year (::params state))
                           year)}
             year])
          (range 2024 2100))]
       ]]
     [:div
      [:label
       {:for "month"
        :class ["block" "text-sm/6" "font-medium" "text-gray-900"]}
       "Month"]
      [:div
       {:class "mt-1"}
       [:select
        {:id "month"
         :name "month"
         :on {:change [[:store/assoc-in [::params :month] :event/target.value-int]
                       [:data/query {:query/kind :query/subscription-statement
                                     :query/data (assoc (::params state)
                                                        :month :event/target.value-int)
                                     }]]}
         :class classes}
        (map
          (fn [month]
            [:option
             {:selected (= (:month (::params state))
                           month)}
             month])
          (range 1 13))]
       ]]]))

(defn render [state]
  (app-frame/app-frame
    state
    [:div
     {:class "px-4 sm:px-6 lg:px-8"}
     [:div
      {:class "sm:flex sm:items-center"}
      [:div
       {:class "sm:flex-auto"}
       [:h1 {:class "text-base font-semibold text-gray-900"} "Statement of account"]
       [:p
        {:class "mt-2 text-sm text-gray-700"}
        "Similar to a bank statement, the lines below show all the events that
        have either increased or decreased your balance this month:"]
       (month-select state)]]

     (let [query (build-query (::params state))]
       (if (query/loading? state
                           query)
         "loading..."
         (when-let [result (query/get-result state
                                             query)]
           (table (assoc state
                         :line-items
                         (:month-overview result))))))]))

(defn current-month
  []
  (let [now (js/Date.)]
    {:year (.getFullYear now)
     :month (inc (.getMonth now))}))

(def page
  {:page-id :pages/subscription-statement
   :route [["ui" "subscription-statement"]]
   :on-load (fn [_location]
              (let [params (current-month)]
                [[:store/assoc ::params params]
                 [:data/query (build-query params)]]))
   :render #'render})
